import React, { useEffect, useRef, useState } from "react";
import UpdatesBanner from "../UpdatesBanner/UpdatesBanner";
import BookDemoForm from "./BookDemoForm";
import { Container } from "@material-ui/core";
import { Col, Row } from "react-bootstrap";

/* ------------------------------------------------------------------ */
/* Design tokens (from Global UX & Design Guidelines)                  */
/* ------------------------------------------------------------------ */
const INK = "#1F2A24";
const MUTED = "#5B6660";
const ACCENT = "#2F5D45";
const ACCENT_TINT = "#E9F0EA";
const HAIRLINE = "#CBD6CE";

const whatWellShowYou = [
  {
    title: "Build & Deploy Your Ecosystem",
    description: "Create and Launch a structured ecosystem network around your business.",
  },
  {
    title: "Orchestrate & Operate Ecosystem",
    description: "Manage & Connect participants, data, digital tools and workflows.",
  },
  /* {
    title: "Operate Your Ecosystem",
    description: "Manage participants, activities and network operations",
  }, */
 /*  {
    title: "Ecosystem Intelligence",
    description:
      "Use AI-driven insights, decision-making and continuous learning.",
  }, */
 /*  {
    title: "Ecosystem Digital Tools",
    description: "Digital Tools for operators and participants.",
  }, */
  {
    title: "Govern & Monetize Your Ecosystem",
    description:
      "Establish governance, enable ecosystem tools and create sustainable network revenue.",
  },
];

/* ------------------------------------------------------------------ */
/* Scoped styles: hover, media queries, motion (can't be done inline)  */
/* ------------------------------------------------------------------ */
const PAGE_STYLES = `
  /* Entrance animation: fade + 14px slide, 300ms ease-out, once */
  .bd-reveal { width: 100%; box-sizing: border-box; opacity: 0; transform: translateY(14px);
    transition: opacity 300ms ease-out, transform 300ms ease-out; }
  .bd-reveal.bd-reveal-in { opacity: 1; transform: none; }

  /* Trim the outer banner container's padding just for this page,
     so the hero isn't taller than other pages' banners.
     Scoped to .bd-banner so it can never affect any other page. */
  .update-banner-container.bd-banner { padding: 48px 0 24px; }
  @media (min-width: 768px) {
    .update-banner-container.bd-banner { padding: 96px 0 40px; }
  }

  /* Hero */
  .bd-hero { padding: 0; }
  .bd-hero-panel { max-width: 860px; margin: 0 auto 28px; text-align: center;
    background: rgba(31, 42, 36, 0.78); border-radius: 16px; padding: 24px 24px; }
  .bd-eyebrow { font-size: 14px; text-transform: uppercase; letter-spacing: 1px;
    font-weight: 600; margin: 0 0 12px; }
  .bd-hero-panel .bd-eyebrow { color: #CFE3D6; }
  .bd-hero h1 { font-size: 36px; font-weight: 700; line-height: 1.1;
    color: #fff; margin: 0 0 12px; }
  .bd-hero-lead { font-size: 18px; line-height: 1.6; color: #fff; margin: 0; }

  /* Card grid with hover elevation */
  .bd-card { background: #fff; border: 1px solid ${HAIRLINE}; border-top: 4px solid ${ACCENT};
    border-radius: 14px; padding: 28px 24px; height: 100%; text-align: left;
    box-shadow: 0 2px 6px rgba(31, 42, 36, 0.08);
    transition: transform 250ms ease-out, box-shadow 250ms ease-out; }
  .bd-card:hover { transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(31, 42, 36, 0.20); }
  .bd-card-num { font-size: 14px; letter-spacing: 1px; text-transform: uppercase;
    font-weight: 600; color: ${MUTED}; margin: 0 0 8px; }
  .bd-card-title { font-size: 21px; font-weight: 600; line-height: 1.3;
    color: ${INK}; margin: 0 0 10px; }
  .bd-card-desc { font-size: 18px; line-height: 1.6; color: ${MUTED}; margin: 0; }

  /* Form section on accent tint, distinct "chapter" */
  .bd-form-section { background: ${ACCENT_TINT}; width: 100%; box-sizing: border-box; padding-top: 56px; padding-bottom: 56px; }
  .bd-section-head { text-align: center; margin: 0 auto 32px; max-width: 720px; }
  .bd-section-head .bd-eyebrow { color: ${MUTED}; }
  .bd-section-head h2 { font-size: 30px; font-weight: 700; line-height: 1.2;
    color: ${INK}; margin: 0; }
  .bd-form-card { background: #fff; border: 1px solid ${HAIRLINE};
    border-radius: 16px; padding: 24px; }

  @media (min-width: 768px) {
    .bd-hero h1 { font-size: 48px; }
    .bd-hero-lead { font-size: 19px; }
    .bd-form-section { padding-top: 96px; padding-bottom: 96px; }
    .bd-section-head h2 { font-size: 36px; }
    .bd-form-card { padding: 40px; }
  }
  @media (min-width: 1200px) {
    .bd-hero h1 { font-size: 52px; }
  }

  /* Respect reduced motion: show content in place, no slide/fade/lift */
  @media (prefers-reduced-motion: reduce) {
    .bd-reveal { opacity: 1; transform: none; transition: none; }
    .bd-card, .bd-card:hover { transition: none; transform: none; }
  }
`;

/* Fade + slide-up once per section, when it scrolls into view */
const Reveal = ({ children, className = "" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect(); // triggered once; no replay on scroll-back
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} bd-reveal${visible ? " bd-reveal-in" : ""}`.trim()}
    >
      {children}
    </div>
  );
};

const bannerContent = (
  <Reveal key="bd-banner" className="book-demo-banner-content bd-hero">
      <div className="bd-hero-panel">
       {/*  <p className="bd-eyebrow">Request a Demo</p> */}
        <h1>How to Build and Own Your Ecosystem Network</h1>
       {/*  <p className="bd-hero-lead">
          Request a demo of GBR Farming&rsquo;s AI-Native Ecosystem
          Orchestration Platform &mdash; and build, orchestrate, operate,
          govern, and scale your own Ecosystem Network.
        </p> */}
      </div>

       <Row className="justify-content-center">
        <Col lg={12}>
          <Row>
            {whatWellShowYou.map((item, index) => (
              <Col md={4} sm={6} xs={12} className="mb-3" key={index}>
                <div className="bd-card">
                  <p className="bd-card-num">
                    {String(index + 1).padStart(2, "0")}
                  </p>
                  <h3 className="bd-card-title">{item.title}</h3>
                  <p className="bd-card-desc">{item.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>

  </Reveal>
);

const BookDemo = () => {
  return (
    <>
      <style>{PAGE_STYLES}</style>

      <UpdatesBanner
        className="agrocare-marketplace-bg bd-banner"
        bannerTitle={[bannerContent]}
      />

      <div className="bd-form-section">
        <Container className="subSectionWrapper" id="AgentForm">
          <Reveal>
            <div className="bd-section-head">
              <p className="bd-eyebrow">Get Started</p>
              <h2>Tell Us About Your Organisation</h2>
            </div>

            <Row className="justify-content-center">
              <Col lg={8} md={10}>
                <div className="bd-form-card">
                  <BookDemoForm />
                </div>
              </Col>
            </Row>
          </Reveal>

          {/* <Row className="justify-content-center text-center mt-5">
            <Col md={8}>
              <h3 className="mb-2">Ready to Explore Your Ecosystem?</h3>
              <p className="mb-3">
                Let&rsquo;s discuss how GBR Farming can help you build, operate
                and scale it.
              </p>
              <a href="#AgentForm" className="Formbutton d-inline-block">
                Request a Demo
              </a>
            </Col>
          </Row> */}
        </Container>
      </div>
    </>
  );
};

export default BookDemo;
